import type { FeatureEvents, ScreenEvent, UIEvent } from '../new-analytics-types';
import { Action, ActionSubject } from '../new-analytics-types';

// https://product-fabric.atlassian.net/wiki/spaces/SA/pages/5553848338/UGC+Data+Use+-+Analytics+Spec#Screen-Events---%5BinlineCard%5D
type ScreenKeys =
  | 'ugcDataUsePageScreenEvent'
  | 'ugcDataUseFocusedTaskSelectAppsScreenEvent'
  | 'ugcDataUseFocusedTaskSelectPreferenceScreenEvent'
  | 'ugcDataUseFocusedTaskReviewScreenEvent';

// https://product-fabric.atlassian.net/wiki/spaces/SA/pages/5553848338/UGC+Data+Use+-+Analytics+Spec#UI-Events---%5BinlineCard%5D
type UIEventKeys =
  | 'ugcDataUseUpdateSettingsClickEvent'
  | 'ugcDataUseConfirmSettingsClickEvent'
  | 'ugcDataUsePageDwelledEvent'
  | 'ugcDataUseFocusedTaskSelectAppsScreenDwelledEvent'
  | 'ugcDataUseFocusedTaskSelectPreferenceScreenDwelledEvent'
  | 'ugcDataUseFocusedTaskReviewScreenDwelledEvent'
  | 'ugcDataUseSelectAllAppsEvent'
  | 'ugcDataUseExpandedFullEvent'
  | 'ugcDataUseExpandedPartialEvent'
  | 'ugcDataUseExpandedLimitedEvent'
  | 'ugcDataUseClickedInfoLinkFromExpandEvent'
  | 'ugcDataUseClickedInfoLinkFromSubtitleEvent';

export enum UgcDataUsePolicyScreens {
  ugcDataUsePageScreen = 'ugcDataUsePageScreen',
  ugcDataUseFocusedTaskSelectAppsScreen = 'ugcDataUseFocusedTaskSelectAppsScreen',
  ugcDataUseFocusedTaskSelectPreferenceScreen = 'ugcDataUseFocusedTaskSelectPreferenceScreen',
  ugcDataUseFocusedTaskReviewScreen = 'ugcDataUseFocusedTaskReviewScreen',
  ugcDataUpdatingScreen = 'ugcDataUpdatingScreen',
}

export const ugcDataUsePolicyEvents: FeatureEvents<ScreenKeys, UIEventKeys, string, string> = {
  confluenceSpec: 'https://product-fabric.atlassian.net/wiki/spaces/SA/pages/5553848338/UGC+Data+Use+-+Analytics+Spec',
  screen: {
    ugcDataUsePageScreenEvent: (attributes: { orgId: string; userId: string }): ScreenEvent => ({
      data: {
        name: UgcDataUsePolicyScreens.ugcDataUsePageScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/85000',
    }),
    ugcDataUseFocusedTaskSelectAppsScreenEvent: (attributes: { orgId: string; userId: string }): ScreenEvent => ({
      data: {
        name: UgcDataUsePolicyScreens.ugcDataUseFocusedTaskSelectAppsScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/82438',
    }),
    ugcDataUseFocusedTaskSelectPreferenceScreenEvent: (attributes: { orgId: string; userId: string }): ScreenEvent => ({
      data: {
        name: UgcDataUsePolicyScreens.ugcDataUseFocusedTaskSelectPreferenceScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/82439',
    }),
    ugcDataUseFocusedTaskReviewScreenEvent: (attributes: { orgId: string; userId: string }): ScreenEvent => ({
      data: {
        name: UgcDataUsePolicyScreens.ugcDataUseFocusedTaskReviewScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/82440',
    }),
  },
  ui: {
    ugcDataUseUpdateSettingsClickEvent: (attributes: { orgId: string; userId: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubjectId: 'ugcDataUseUpdateSettings',
        actionSubject: ActionSubject.Button,
        source: UgcDataUsePolicyScreens.ugcDataUpdatingScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/82244',
    }),
    ugcDataUseConfirmSettingsClickEvent: (attributes: { orgId: string; userId: string; workspaceIds: Set<string> }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubjectId: 'ugcDataUseConfirmSettings',
        actionSubject: ActionSubject.Button,
        source: UgcDataUsePolicyScreens.ugcDataUpdatingScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/82243',
    }),
    ugcDataUsePageDwelledEvent: (attributes: { orgId: string; userId: string }): UIEvent => ({
      data: {
        action: Action.Dwelled,
        actionSubjectId: 'ugcDataUsePageDwelled',
        actionSubject: ActionSubject.Page,
        source: UgcDataUsePolicyScreens.ugcDataUsePageScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/82638',
    }),
    ugcDataUseFocusedTaskSelectAppsScreenDwelledEvent: (attributes: { orgId: string; userId: string }): UIEvent => ({
      data: {
        action: Action.Dwelled,
        actionSubjectId: 'ugcDataUseFocusedTaskSelectAppsScreenDwelled',
        actionSubject: ActionSubject.Page,
        source: UgcDataUsePolicyScreens.ugcDataUseFocusedTaskSelectAppsScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/82639',
    }),
    ugcDataUseFocusedTaskSelectPreferenceScreenDwelledEvent: (attributes: { orgId: string; userId: string }): UIEvent => ({
      data: {
        action: Action.Dwelled,
        actionSubjectId: 'ugcDataUseFocusedTaskSelectPreferenceScreenDwelled',
        actionSubject: ActionSubject.Page,
        source: UgcDataUsePolicyScreens.ugcDataUseFocusedTaskSelectPreferenceScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/82640',
    }),
    ugcDataUseFocusedTaskReviewScreenDwelledEvent: (attributes: { orgId: string; userId: string }): UIEvent => ({
      data: {
        action: Action.Dwelled,
        actionSubjectId: 'ugcDataUseFocusedTaskReviewScreenDwelled',
        actionSubject: ActionSubject.Page,
        source: UgcDataUsePolicyScreens.ugcDataUseFocusedTaskReviewScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/82641',
    }),
    ugcDataUseSelectAllAppsEvent: (attributes: { orgId: string; userId: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubjectId: 'ugcDataUseSelectAllApps',
        actionSubject: ActionSubject.Checkbox,
        source: UgcDataUsePolicyScreens.ugcDataUseFocusedTaskSelectAppsScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/85166',
    }),
    ugcDataUseExpandedFullEvent: (attributes: { orgId: string; userId: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubjectId: 'ugcDataUseExpandedFull',
        actionSubject: ActionSubject.Button,
        source: UgcDataUsePolicyScreens.ugcDataUseFocusedTaskSelectPreferenceScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/85172',
    }),
    ugcDataUseExpandedPartialEvent: (attributes: { orgId: string; userId: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubjectId: 'ugcDataUseExpandedPartial',
        actionSubject: ActionSubject.Button,
        source: UgcDataUsePolicyScreens.ugcDataUseFocusedTaskSelectPreferenceScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/85173',
    }),
    ugcDataUseExpandedLimitedEvent: (attributes: { orgId: string; userId: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubjectId: 'ugcDataUseExpandedLimited',
        actionSubject: ActionSubject.Button,
        source: UgcDataUsePolicyScreens.ugcDataUseFocusedTaskSelectPreferenceScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/85174',
    }),
    ugcDataUseClickedInfoLinkFromExpandEvent: (attributes: { orgId: string; userId: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubjectId: 'ugcDataUseClickedInfoLinkFromExpand',
        actionSubject: ActionSubject.Link,
        source: UgcDataUsePolicyScreens.ugcDataUseFocusedTaskSelectPreferenceScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/85175',
    }),
    ugcDataUseClickedInfoLinkFromSubtitleEvent: (attributes: { orgId: string; userId: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubjectId: 'ugcDataUseClickedInfoLinkFromSubtitle',
        actionSubject: ActionSubject.Link,
        source: UgcDataUsePolicyScreens.ugcDataUseFocusedTaskSelectPreferenceScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/85176',
    }),
  },
  track: {},
  operational: {},
};
